import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ScanBarcode, Sparkles, Printer, Barcode } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { API_URL } from "../../lib/utils";
import { barcodeSvg, isValidEan13, normalizeBarcode } from "../../lib/barcode";
import { printBarcodeLabels } from "../../lib/labelPrint";

// Campo de código de barras no formulário do produto: escrever, ler com o leitor (que escreve + Enter) ou gerar um interno
export const BarcodeField = ({ value, onChange, productId, headers, onGenerated }) => {
  const [busy, setBusy] = useState(false);
  const code = normalizeBarcode(value);
  const svg = useMemo(() => barcodeSvg(code, { height: 36, width: 1.5, fontSize: 12 }), [code]);

  const generate = async () => {
    if (!productId) { toast.info("Guarde o produto primeiro para gerar um código interno"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/products/${productId}/barcode/generate`, {}, { headers, withCredentials: true });
      onChange(r.data.barcode); onGenerated?.(r.data.barcode);
      toast.success(`Código ${r.data.barcode} gerado`);
    } catch (e) { toast.error(e.response?.data?.detail || "Não foi possível gerar"); }
    finally { setBusy(false); }
  };

  return (
    <Card data-testid="product-barcode-card">
      <CardHeader className="py-3"><CardTitle className="text-base flex items-center gap-2"><ScanBarcode className="w-4 h-4" /> Código de Barras</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <div className="flex-1 space-y-1">
            <Label htmlFor="product-barcode">EAN-13 / UPC / Code128 — leia com o leitor ou escreva</Label>
            <Input id="product-barcode" value={value || ""} onChange={(e) => onChange(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }} placeholder="Ex.: 5601234567890" inputMode="numeric" autoComplete="off" data-testid="product-barcode-input" />
          </div>
          <Button type="button" variant="outline" className="self-end gap-1.5" onClick={generate} disabled={busy} data-testid="product-barcode-generate-btn"><Sparkles className="w-4 h-4" /> Gerar interno</Button>
        </div>
        {code && (
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-md p-1.5 border" dangerouslySetInnerHTML={{ __html: svg }} data-testid="product-barcode-preview" />
            <p className="text-xs text-muted-foreground">{/^\d{13}$/.test(code) ? (isValidEan13(code) ? "EAN-13 válido" : "13 dígitos mas o dígito de controlo não confere — confirme o código") : "Será impresso em Code128"}</p>
          </div>
        )}
        <p className="text-xs text-muted-foreground">Sem código do fabricante? «Gerar interno» cria um EAN-13 de uso interno (prefixo 200) para imprimir etiquetas.</p>
      </CardContent>
    </Card>
  );
};

// Barra de acções na lista de produtos: etiquetas e geração em lote
export const BarcodeToolbar = ({ products, settings, headers, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [copies, setCopies] = useState(1);
  const withCode = products.filter((p) => p.barcode).length;
  const missing = products.length - withCode;

  const generateMissing = async () => {
    if (!window.confirm(`Gerar códigos de barras internos para ${missing} produto(s) sem código?`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/products/barcodes/generate-missing`, {}, { headers, withCredentials: true });
      toast.success(`${r.data.generated} código(s) gerado(s)`); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao gerar"); }
    finally { setBusy(false); }
  };

  const print = () => {
    if (!printBarcodeLabels(products, settings, { copies })) toast.info("Nenhum produto da selecção tem código de barras");
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2 text-sm" data-testid="barcode-toolbar">
      <Barcode className="w-4 h-4 text-muted-foreground" />
      <span className="text-muted-foreground">{withCode} com código · {missing} sem código</span>
      {missing > 0 && <Button size="sm" variant="outline" onClick={generateMissing} disabled={busy} data-testid="barcode-generate-missing-btn"><Sparkles className="w-3.5 h-3.5 mr-1" /> Gerar em falta</Button>}
      <div className="ml-auto flex items-center gap-2">
        <Label htmlFor="label-copies" className="text-xs">Cópias</Label>
        <Input id="label-copies" type="number" min={1} max={20} value={copies} onChange={(e) => setCopies(Math.max(1, Math.min(20, Number(e.target.value) || 1)))} className="h-8 w-16" data-testid="barcode-label-copies" />
        <Button size="sm" onClick={print} disabled={!withCode} data-testid="barcode-print-labels-btn"><Printer className="w-3.5 h-3.5 mr-1" /> Imprimir etiquetas ({withCode})</Button>
      </div>
    </div>
  );
};
